import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { supabase } from '@/lib/supabase'

// ─── Types minimaux ───────────────────────────────────────────────────────────

interface PrayerRow {
  id: string
  title: string
  recurrence_type: 'none' | 'daily' | 'weekly' | 'monthly'
  recurrence_day: number | null
  reminder_time: string | null
  created_at: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convertit un UUID en entier 24 bits pour Capacitor.
 * Suffisamment unique pour une liste de prières typique.
 */
export function prayerNotifId(prayerId: string): number {
  return parseInt(prayerId.replace(/-/g, '').slice(-6), 16)
}

/**
 * Calcule la prochaine occurrence de l'heure donnée (format "HH:mm").
 *
 * - daily   : aujourd'hui à cette heure, ou demain si déjà passé
 * - weekly  : prochain `targetWeekday` (0=Lundi … 6=Dimanche, convention Selah)
 * - monthly : prochain `targetDayOfMonth` (1-31)
 */
function nextOccurrence(
  timeStr: string,
  targetWeekday?: number,
  targetDayOfMonth?: number,
): Date {
  const [h, m] = timeStr.split(':').map(Number)
  const now    = new Date()
  const result = new Date(now)
  result.setHours(h, m, 0, 0)

  if (targetWeekday !== undefined) {
    // Convention Selah : 0=Lundi → JS getDay() : 1=Lundi
    const jsTarget  = (targetWeekday + 1) % 7   // 0→1, 6→0 (dimanche)
    const jsToday   = now.getDay()
    let daysUntil   = (jsTarget - jsToday + 7) % 7
    if (daysUntil === 0 && result <= now) daysUntil = 7
    result.setDate(result.getDate() + daysUntil)
  } else if (targetDayOfMonth !== undefined) {
    result.setDate(targetDayOfMonth)
    if (result <= now) {
      result.setMonth(result.getMonth() + 1)
      result.setDate(targetDayOfMonth)
    }
  } else {
    // daily : si l'heure est déjà passée aujourd'hui, décaler à demain
    if (result <= now) result.setDate(result.getDate() + 1)
  }

  return result
}

// ─── Fonction principale ──────────────────────────────────────────────────────

/**
 * Planifie (ou replannifie) les notifications locales pour toutes les prières
 * actives avec une récurrence et une heure de rappel.
 *
 * - Annule d'abord les notifications existantes pour éviter les doublons.
 * - Sans effet sur le web ou en dehors des plateformes natives.
 */
export async function scheduleRecurringPrayerNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  // 1. Demande de permission (no-op si déjà accordée)
  const { display } = await LocalNotifications.requestPermissions()
  if (display !== 'granted') return

  // 2. Récupère les prières actives avec récurrence + heure de rappel
  // Cast en `any` : recurrence_type/recurrence_day/reminder_time ne sont pas
  // encore dans database.types.ts (schéma étendu après génération des types).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pi = supabase.from('prayer_item') as any
  const { data, error } = await pi
    .select('id, title, recurrence_type, recurrence_day, reminder_time, created_at')
    .eq('status', 'active')
    .neq('recurrence_type', 'none')
    .not('reminder_time', 'is', null)

  if (error || !data) return

  const prayers = data as PrayerRow[]

  // 3. Annule TOUTES les notifications locales orphelines (prières supprimées)
  //    + celles des prières actives (pour éviter les doublons avant replannification)
  const validIds = new Set(prayers.map(p => prayerNotifId(p.id)))
  try {
    const pending = await LocalNotifications.getPending()
    const toCancel = pending.notifications.filter(n =>
      n.extra?.prayerId || validIds.has(n.id),
    )
    if (toCancel.length > 0) {
      await LocalNotifications.cancel({ notifications: toCancel.map(n => ({ id: n.id })) })
    }
  } catch { /* ignore */ }

  if (prayers.length === 0) return

  // 4. Construit le tableau de notifications
  const notifications = prayers
    .filter(p => p.reminder_time)
    .map(p => {
      let at: Date
      let every: 'day' | 'week' | 'month'

      switch (p.recurrence_type) {
        case 'daily':
          at    = nextOccurrence(p.reminder_time!)
          every = 'day'
          break
        case 'weekly':
          at    = nextOccurrence(p.reminder_time!, p.recurrence_day ?? 0)
          every = 'week'
          break
        case 'monthly': {
          // Jour du mois basé sur le jour de création de la prière
          const dayOfMonth = new Date(p.created_at).getDate()
          at    = nextOccurrence(p.reminder_time!, undefined, dayOfMonth)
          every = 'month'
          break
        }
        default:
          return null
      }

      return {
        id:           prayerNotifId(p.id),
        title:        'Temps de prière',
        body:         p.title,
        schedule:     { at, every, allowWhileIdle: true },
        sound:        undefined,
        attachments:  undefined,
        actionTypeId: '',
        extra:        { prayerId: p.id },
      }
    })
    .filter((n): n is NonNullable<typeof n> => n !== null)

  if (notifications.length > 0) {
    await LocalNotifications.schedule({ notifications })
  }
}
